import React, { FC, Fragment, memo, useEffect, useMemo, useRef } from 'react';
import { useLocation, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Form } from '@components/atoms/Form';
import { Input, InputTypes, InputVariants } from '@components/atoms/Input';
import { AppliedFilters } from '../AppliedFilters';
import { RadioFilter } from '../RadioFilter';
import { CheckboxFilter } from '../CheckboxFilter';
import { RangeFilter } from '../RangeFilter';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { FilterColors } from '../FilterColors';
import { useData } from '@hooks/useData';
import { useFilterBuilder } from '@hooks/useFilterBuilder';
import { useGetAllProductStores } from '@services/useGetAllProductStores';
import { useFilters } from '@hooks/useFilters';
import { useGetAllProductAttributesByCategory } from '@services/useGetAllProductAttributesByCategory';
import { useGetAllCategoriesWithProducts } from '@services/useGetAllCategoriesWithProducts';
import { flattenCategories } from '@utils/helper';
import { useGetProducts } from '@services/useGetProducts';
import { useFilterBuilderText } from '@hooks/useFilterBuilderText';
import { useFilterBuilderPrice } from '@hooks/useFilterBuilderPrice';
import { SITE_URLS } from '@constants';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { Modules, useModule } from '@hooks/useModule';
import { useGetProductCategoryMaxPrice } from '@services/useGetProductCategoryMaxPrice';
import { useGetZones } from '@services/useGetZones';
import { useGetCampaigns } from '@services/useGetCampaigns';
import { ProductCardProps } from '@components/molecules/ProductCard';
import { FavoriteFilter } from '../FavoriteFilter';

export interface FilterSidebarProductsProps {
    isCategoryPage?: boolean;
    isSearchResultsPage: boolean;
    pageToRequest: number;
    checkPageToRequest: () => void;
}

export const FilterSidebarProducts: FC<FilterSidebarProductsProps> = ({
    isCategoryPage,
    isSearchResultsPage,
    pageToRequest,
    checkPageToRequest,
}) => {
    const location = useLocation();
    const { navigate } = useCustomNavigate();
    const { searchQuery, categoryUuid } = useParams();
    const { login, allCategoriesWithProducts, isLoadingAllCategoriesWithProducts, products, isLoadingProducts, setProducts } =
        useData();
    const { request: requestAllCategoriesWithProducts } = useGetAllCategoriesWithProducts();
    const { request: requestProducts } = useGetProducts();
    const { appliedFilters, results, setResults, currentPage, setCurrentPage } = useFilters();
    const { isModuleActive } = useModule();
    const { t, i18n } = useTranslation();

    const translations = {
        radioSearchProducts: t('filter-sidebar-products-components.radio-products-search.label'),
        radioSearchServices: t('filter-sidebar-products-components.radio-services-search.label'),
        // colorsLabel: t('filter-sidebar-products-components.filter-colors.label'),
    };

    useEffect(() => {
        // when unmounting page, reset results
        // to prevent showing old results when returning to this page
        return () => {
            setResults([]);
            setProducts(null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // call services, if we don't have the information yet
    useEffect(() => {
        !allCategoriesWithProducts && !isLoadingAllCategoriesWithProducts && requestAllCategoriesWithProducts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allCategoriesWithProducts]);

    const formRef = useRef<HTMLFormElement>(null);

    const queryParams = new URLSearchParams(location.search);
    const getTextParam = queryParams.get('text') || '';
    const getStoresParam = queryParams.get('stores') || '';
    const getCategoriesParam = queryParams.get('categories') || '';
    const getTypeParam = queryParams.get('type') || '';
    const getMinParam = queryParams.get('min') || '';
    const getMaxParam = queryParams.get('max') || '';
    const getOrderParam = queryParams.get('order') || '';
    const getZonesParam = queryParams.get('zones') || '';
    const getRatingsParam = queryParams.get('rating') || '';
    const favoriteParam = queryParams.get('favorites') === 'favorite-only';

    // the attribute values params are all other parameters
    const excludedParams = ['order', 'text', 'stores', 'categories', 'type', 'min', 'max', 'rating', 'favorites'];
    const attributesValuesParams: string[] = [];
    queryParams.forEach((value, key) => {
        if (!excludedParams.includes(key)) {
            attributesValuesParams.push(value);
        }
    });

    const getCampaignsParam = queryParams.get('campaigns_uuid') || '';

    const fetchProducts = (page: number) => {
        setCurrentPage(page);

        // prevent calls for pages that don't exist
        if (products?.pagination?.total_pages && page > products?.pagination?.total_pages) {
            return;
        }

        // NOTE: if categoryUuid is defined, means user is in products page associated with a category
        // if not, means he is either in general products page, or products search results page
        const categoriesUuids = categoryUuid ? [categoryUuid] : getCategoriesParam ? getCategoriesParam.split(',') : [];
        // NOTE: if searchQuery is defined, means user is in products search results page
        // if not, means he is either in general products page, or products page associated with a category
        const textSearch = searchQuery || getTextParam;
        const storesUuids = getStoresParam ? getStoresParam.split(',') : [];
        const zonesUuids = getZonesParam ? getZonesParam.split(',') : [];
        const ratings = getRatingsParam ? getRatingsParam.split(',') : [];
        const attributesValuesUuids = attributesValuesParams.map((item) => item.split(','));
        const types = getTypeParam ? getTypeParam.split(',') : [];
        const campaignsUuids = getCampaignsParam ? getCampaignsParam.split(',') : [];

        requestProducts({
            textSearch: textSearch,
            stores_uuid: storesUuids,
            categories_uuid: categoriesUuids,
            zone_uuid: zonesUuids,
            attribute_values_uuids: attributesValuesUuids.flat().filter((item) => item !== ''),
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            type: types as any,
            min_price: Number(getMinParam) || undefined,
            max_price: Number(getMaxParam) || undefined,
            page: page,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            order: getOrderParam as any,
            campaigns_uuid: campaignsUuids,
            ratings: ratings,
            favorite: favoriteParam,
        });
    };

    // request more products from next page
    useEffect(() => {
        pageToRequest > currentPage && !isLoadingProducts && fetchProducts(currentPage + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageToRequest]);

    // request first page whenever a filter changes
    const submitForm = () => fetchProducts(1);

    useEffect(() => {
        !isLoadingProducts && formRef?.current?.requestSubmit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appliedFilters, searchQuery, categoryUuid]);

    // every time the endpoint is called, append the data to results constant
    // if the page being called is the first page, reset results
    useEffect(() => {
        setResults((previous: ProductCardProps[]) => {
            const currentPageData = (products?.data || []).map((item) => {
                return { ...item, initialFavorite: item.favorite || false };
            });

            return products?.pagination?.current_page === 1 ? currentPageData : [...previous, ...currentPageData];
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [products, i18n.language]);

    // manually check if next page should be requested
    // in case user's screen is big and isn't possible to scroll to trigger next page request
    useEffect(() => {
        checkPageToRequest();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [results.length]);

    return (
        <>
            {isCategoryPage && <AppliedFilters />}

            <Form formRef={formRef} classes="d-flex flex-column gap-4" onSubmit={submitForm}>
                {isSearchResultsPage ? (
                    <RadioFilter
                        options={[
                            {
                                id: 'product-option',
                                label: translations.radioSearchProducts,
                                checked: true,
                                value: 'product-search',
                                onClick: () => navigate(`/${SITE_URLS.productsSearch.replace(':searchQuery', `${searchQuery}`)}`),
                            },
                            {
                                id: 'service-option',
                                label: translations.radioSearchServices,
                                value: 'service-search',
                                onClick: () => navigate(`/${SITE_URLS.servicesSearch.replace(':searchQuery', `${searchQuery}`)}`),
                            },
                        ]}
                    />
                ) : (
                    <TextFilter />
                )}

                <>{isModuleActive(Modules.favorite) && login !== null && <FavoriteFilter />}</>

                <>{!isCategoryPage && <CategoriesFilters />}</>

                <SaleTypeFilters />

                {/* #TODO: get max price from products service */}
                <PriceFilter />

                {/* #TODO: this feature will be displayed later in development
                isCategoryPage && (
                    <FilterColors
                        colors={[
                            {
                                color: '#61B32A',
                                checked:
                                    appliedFilters
                                        .find((item) => item.label === translations.colorsLabel)
                                        ?.values.includes('#61B32A') || false,
                            },
                            {
                                color: '#39B0AC',
                                checked:
                                    appliedFilters
                                        .find((item) => item.label === translations.colorsLabel)
                                        ?.values.includes('#39B0AC') || false,
                            },
                        ]}
                        onChange={(value) => {
                            toggleFilterValue(translations.colorsLabel, value);
                        }}
                    />
                ) */}

                <>{isCategoryPage ? <VariantsFilters /> : <></>}</>

                <>{isModuleActive(Modules.rating) && <RatingFilter />}</>

                <StoresFilters />

                <ZonesFilter />

                <CampaignsFilter />
            </Form>
        </>
    );
};

const TextFilter: FC = () => {
    const { appliedFilters, setFilterValues } = useFilters();
    const { t } = useTranslation();

    const translations = {
        title: t('filter-sidebar-products-components.filter-text-search.title'),
        placeholder: t('filter-sidebar-products-components.text-search.placeholder'),
    };

    const paramName = 'text';
    const title = translations.title;

    const { search, setSearch } = useFilterBuilderText({
        paramName,
        appliedValue: appliedFilters.find((item) => item.label === title)?.values[0].name || '',
        setFilterValues: (data) => setFilterValues(title, data),
    });

    return (
        <Input
            type={InputTypes.text}
            variant={InputVariants.searchInput}
            placeholder={translations.placeholder}
            value={search || ''}
            onChange={(value) => setSearch(value)}
        />
    );
};

const PriceFilter: FC = memo(() => {
    const { categoryUuid } = useParams();
    const { request: requestMaxPrice, loading: loadingMaxPrice } = useGetProductCategoryMaxPrice();
    const { productCategoryMaxPrice } = useData();
    const { appliedFilters, setFilterValues } = useFilters();

    const { t } = useTranslation();

    const translations = {
        title: t('filter-sidebar-products-components.filter-range.title'),
    };

    useEffect(() => {
        requestMaxPrice({ uuid: categoryUuid });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryUuid]);

    const title = translations.title;

    const appliedValue = appliedFilters.find((item) => item.label === title)?.values[0].name || '';
    const splitAppliedValues = appliedValue.split(' - ');
    const appliedMinValue = appliedValue ? splitAppliedValues[0]?.replace(/€/g, '').trim() || '' : '';
    const appliedMaxValue = appliedValue ? splitAppliedValues[1]?.replace(/€/g, '').trim() || '' : '';

    const maxPrice = productCategoryMaxPrice?.data?.max ? Number(productCategoryMaxPrice?.data?.max) : null;

    const { min, max, setMin, setMax } = useFilterBuilderPrice({
        appliedValues: { min: appliedValue ? Number(appliedMinValue) : null, max: appliedValue ? Number(appliedMaxValue) : null },
        setFilterValues: (min, max) =>
            setFilterValues(
                title,
                min === null && max === null ? [] : [{ uuid: null, name: `${min || 0}€ - ${max || maxPrice}€` }]
            ),
    });

    return (
        <>
            {maxPrice && !loadingMaxPrice && (
                <RangeFilter
                    maxInput={maxPrice}
                    minValue={Number(min) || 0}
                    maxValue={Number(max) || maxPrice}
                    step={5}
                    labelSuffix="€"
                    setMinValue={setMin}
                    setMaxValue={setMax}
                />
            )}
        </>
    );
});

const CategoriesFilters: FC = () => {
    const { allCategoriesWithProducts } = useData();
    const { request: requestAllCategoriesWithProducts } = useGetAllCategoriesWithProducts();
    const { appliedFilters, setFilterValues } = useFilters();
    const { t } = useTranslation();

    const translations = {
        title: t('filter-sidebar-products-components.checkbox-categories.title'),
    };

    const paramName = 'categories';
    const title = translations.title;

    const { getOptions, toggleValue } = useFilterBuilder([
        {
            paramName,
            labelField: 'name',
            data: { data: flattenCategories(allCategoriesWithProducts?.data || []) },
            requestData: requestAllCategoriesWithProducts,
            appliedValues: appliedFilters.find((item) => item.label === title)?.values.map((value) => value.name) || [],
            setFilterValues: (data) => setFilterValues(title, data),
        },
    ]);

    const options = getOptions(paramName);

    return (
        <>{options && <CheckboxFilter title={title} options={options} onChange={(value) => toggleValue(paramName, value)} />}</>
    );
};

export const CampaignsFilter: FC = () => {
    const { campaigns } = useData();
    const { request: requestAllCampaigns, loading: isLoadingAllCampaigns } = useGetCampaigns();
    const { appliedFilters, setFilterValues } = useFilters();
    const { t } = useTranslation();

    const translations = {
        title: t('filter-sidebar-products-components.checkbox-campaigns.title'),
    };

    const paramName = 'campaigns_uuid';
    const title = translations.title;

    useEffect(() => {
        if (!campaigns && !isLoadingAllCampaigns) {
            requestAllCampaigns();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaigns]);

    const memoizedCampaignOptions = useMemo(() => {
        if (!campaigns?.data) {
            return [];
        }

        return campaigns.data.map((campaign) => ({
            uuid: campaign.uuid || '',
            name: campaign.title || '',
        }));
    }, [campaigns]);

    const { getOptions, toggleValue } = useFilterBuilder([
        {
            paramName,
            labelField: 'name',
            data: { data: memoizedCampaignOptions },
            requestData: requestAllCampaigns,
            appliedValues:
                appliedFilters
                    .find((item) => item.label === title)
                    ?.values.map((value) => value.uuid)
                    .filter((uuid): uuid is string => uuid !== null && uuid !== undefined) || [],
            setFilterValues: (data) => setFilterValues(title, data),
        },
    ]);

    const options = getOptions(paramName);

    return (
        <>
            {options && options.length > 0 && (
                <CheckboxFilter title={title} options={options} onChange={(value) => toggleValue(paramName, value)} />
            )}
        </>
    );
};

const SaleTypeFilters: FC = () => {
    const { appliedFilters, setFilterValues } = useFilters();
    const { t } = useTranslation();

    const translations = {
        title: t('filter-sidebar-products-components.checkbox-typology.title'),
        typeStock: t('filter-sidebar-products-components.checkbox-typology.stock-label'),
        typeDirect: t('filter-sidebar-products-components.checkbox-typology.direct-label'),
        typePromotion: t('filter-sidebar-products-components.checkbox-typology.promotion-label'),
    };

    const paramName = 'type';
    const title = translations.title;

    const { getOptions, toggleValue } = useFilterBuilder([
        {
            paramName,
            labelField: 'name',
            data: {
                data: [
                    { name: translations.typeStock, uuid: 'in_stock' },
                    { name: translations.typeDirect, uuid: 'direct_sale' },
                    { name: translations.typePromotion, uuid: 'with_discount' },
                ],
            },
            requestData: null,
            appliedValues: appliedFilters.find((item) => item.label === title)?.values.map((value) => value.name) || [],
            setFilterValues: (data) => setFilterValues(title, data),
        },
    ]);

    const options = getOptions(paramName);

    return (
        <>{options && <CheckboxFilter title={title} options={options} onChange={(value) => toggleValue(paramName, value)} />}</>
    );
};

const VariantsFilters: FC = () => {
    const { categoryUuid } = useParams();
    const { allProductAttributesByCategory } = useData();
    const { request: requestAllProductAttributesByCategory } = useGetAllProductAttributesByCategory();
    const { appliedFilters, setFilterValues } = useFilters();
    const { i18n } = useTranslation();

    // call services, if we don't have the information yet
    useEffect(() => {
        categoryUuid && requestAllProductAttributesByCategory({ uuid: categoryUuid });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryUuid, i18n.language]);

    const { getOptions, toggleValue } = useFilterBuilder(
        allProductAttributesByCategory?.data?.map((item) => {
            const title = item.name || '';

            return {
                paramName: item.name || '',
                labelField: 'value',
                data: { data: item.values || [] },
                requestData: null,
                appliedValues: appliedFilters.find((item) => item.label === title)?.values.map((value) => value.name) || [],
                setFilterValues: (data) => setFilterValues(title, data),
            };
        }) || []
    );

    return (
        <>
            {allProductAttributesByCategory?.data?.map((item) => {
                const options = getOptions(item.name || '');

                return (
                    <Fragment key={item.uuid}>
                        {options && (
                            <CheckboxFilter
                                title={item.name || ''}
                                options={options}
                                onChange={(value) => toggleValue(item.name || '', value)}
                            />
                        )}
                    </Fragment>
                );
            })}
        </>
    );
};

const StoresFilters: FC = () => {
    const { allProductStores } = useData();
    const { request: requestAllStores } = useGetAllProductStores();
    const { appliedFilters, setFilterValues } = useFilters();
    const { t } = useTranslation();

    const translations = {
        title: t('filter-sidebar-products-components.checkbox-merchant.title'),
    };

    const paramName = 'stores';
    const title = translations.title;

    const { getOptions, toggleValue } = useFilterBuilder([
        {
            paramName,
            labelField: 'commercial_name',
            data: allProductStores,
            requestData: requestAllStores,
            appliedValues: appliedFilters.find((item) => item.label === title)?.values.map((value) => value.name) || [],
            setFilterValues: (data) => setFilterValues(title, data),
        },
    ]);

    const options = getOptions(paramName);

    return (
        <>{options && <CheckboxFilter title={title} options={options} onChange={(value) => toggleValue(paramName, value)} />}</>
    );
};

export const RatingFilter: FC = () => {
    const { appliedFilters, setFilterValues } = useFilters();
    const { t } = useTranslation();

    const translations = {
        title: t('rating-filter-component.title'),
        filterText: (stars: number) => t('filter-sidebar-products-components.filter-rating.text', { count: stars }),
    };

    const starsVotes = [
        { stars: 5 /*, votes: 377*/ },
        { stars: 4 /*, votes: 456*/ },
        { stars: 3 /*, votes: 621*/ },
        { stars: 2 /*, votes: 51*/ },
        { stars: 1 /*, votes: 3*/ },
    ];

    const maxStars: number = Math.max.apply(
        null,
        starsVotes.map((item) => item.stars)
    );

    const paramName = 'rating';
    const title = translations.title;

    const { getOptions, toggleValue } = useFilterBuilder([
        {
            paramName,
            labelField: 'stars',
            data: {
                data: starsVotes.map((item) => {
                    return {
                        stars: (
                            <>
                                {Array.from(Array(item.stars).keys()).map((index) => (
                                    <i className="fa-solid fa-star" key={`solid-${index}`} />
                                ))}

                                {Array.from(Array(maxStars - item.stars).keys()).map((index) => (
                                    <i className="fa-light fa-star" key={`light-${index}`} />
                                ))}

                                {/*<Typography classes="ms-2" tagVariant={TypographyTagVariants.span}>
                                    <>{item.votes}</>
                                </Typography>*/}
                            </>
                        ),
                        uuid: `${item.stars}`,
                    };
                }),
            },
            requestData: null,
            appliedValues: appliedFilters.find((item) => item.label === title)?.values.map((value) => value.name) || [],
            setFilterValues: (data) =>
                setFilterValues(
                    title,
                    data.map((item) => {
                        return { uuid: item.uuid, name: translations.filterText(Number(item.uuid)) };
                    })
                ),
        },
    ]);

    const options = getOptions(paramName);

    return (
        <div className="rating">
            {options && <CheckboxFilter title={title} options={options} onChange={(value) => toggleValue(paramName, value)} />}
        </div>
    );
};

const ZonesFilter: FC = () => {
    const { allZones } = useData();
    const { request: requestAllZones } = useGetZones();
    const { appliedFilters, setFilterValues } = useFilters();
    const { t } = useTranslation();
    const { isModuleActive } = useModule();

    const translations = {
        title: t('filter-sidebar-merchants-component.zones.title'),
    };

    useEffect(() => {
        if (!allZones && isModuleActive(Modules.zones)) {
            requestAllZones();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allZones]);

    const paramName = 'zones';
    const title = translations.title;

    const { getOptions, toggleValue } = useFilterBuilder(
        allZones
            ? [
                  {
                      paramName,
                      labelField: 'name',
                      data: { data: allZones.data || [] },
                      requestData: null,
                      appliedValues: appliedFilters.find((item) => item.label === title)?.values.map((v) => v.name) || [],
                      setFilterValues: (data) => setFilterValues(title, data),
                  },
              ]
            : []
    );

    const options = getOptions(paramName);

    return (
        <>
            {isModuleActive(Modules.zones) && allZones && options && (
                <CheckboxFilter title={title} options={options} onChange={(value) => toggleValue(paramName, value)} />
            )}
        </>
    );
};
